import { API_URL } from '../config.js';

class ReportService {
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
  }

  // ==================== Query Exploration ====================

  async getColumns(plantId, table, { dbEngine, mysqlDatabase } = {}) {
    const params = new URLSearchParams();
    if (dbEngine) params.set('dbEngine', dbEngine);
    if (mysqlDatabase) params.set('mysqlDatabase', mysqlDatabase);
    const qs = params.toString() ? `?${params}` : '';
    const res = await fetch(`${API_URL}/reports/columns/${plantId}/${table}${qs}`, {
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.columns;
  }

  async getDistinctValues(plantId, table, column, { dbEngine, mysqlDatabase } = {}) {
    const params = new URLSearchParams();
    if (dbEngine) params.set('dbEngine', dbEngine);
    if (mysqlDatabase) params.set('mysqlDatabase', mysqlDatabase);
    const qs = params.toString() ? `?${params}` : '';
    const res = await fetch(`${API_URL}/reports/distinct/${plantId}/${table}/${column}${qs}`, {
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.values;
  }

  async preview(sql, plantId, { dbEngine, mysqlDatabase } = {}) {
    const res = await fetch(`${API_URL}/reports/preview`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ sql, plantId, dbEngine, mysqlDatabase })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data;
  }

  // ==================== Export ====================

  async getExportLimits() {
    const res = await fetch(`${API_URL}/reports/export/limits`, {
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data;
  }


  async exportReport(sql, plantId, format, filename, { dbEngine, mysqlDatabase } = {}) {
    const res = await fetch(`${API_URL}/reports/export`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ sql, plantId, format, filename, dbEngine, mysqlDatabase })
    });

    if (!res.ok) {
      let msg = 'Export failed';
      try { const err = await res.json(); msg = err.message || msg; } catch {}
      throw new Error(msg);
    }

    // Server promoted to async job (estimated rows over threshold).
    // Returns JSON { async:true, jobId, status, estimatedRows, threshold }.
    if (res.status === 202) {
      const job = await res.json();
      return { kind: 'job', ...job };
    }

    const blob = await res.blob();
    return { kind: 'file', blob };
  }

  async exportAppend(fileBase64, sql, plantId, { dbEngine, mysqlDatabase } = {}) {
    const res = await fetch(`${API_URL}/reports/export/append`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ fileBase64, sql, plantId, dbEngine, mysqlDatabase })
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.message || 'Append failed');
    }

    const blob = await res.blob();
    return blob;
  }

  // ==================== Scheduled Reports ====================

  async getScheduledReports() {
    const res = await fetch(`${API_URL}/reports/scheduled`, {
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.reports;
  }

  async createScheduledReport(config) {
    // config may include dbEngine and mysqlDatabase — pass through as-is
    const res = await fetch(`${API_URL}/reports/scheduled`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(config)
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.report;
  }

  async deleteScheduledReport(id) {
    const res = await fetch(`${API_URL}/reports/scheduled/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
  }

  async pauseScheduledReport(id) {
    const res = await fetch(`${API_URL}/reports/scheduled/${id}/pause`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
  }

  async resumeScheduledReport(id) {
    const res = await fetch(`${API_URL}/reports/scheduled/${id}/resume`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
  }

  // ==================== Generated Files ====================

  async getReportFiles() {
    const res = await fetch(`${API_URL}/reports/files`, {
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    // Returns the file list (back-compat). Use getExportJobs() for the
    // in-flight async-export jobs surfaced alongside in the same response.
    return data.files;
  }

  async getExportJobs() {
    // Same endpoint as getReportFiles() but pulls the parallel `jobs` array
    // so the inbox can render "Running 23%" rows without a second round-trip.
    const res = await fetch(`${API_URL}/reports/files`, {
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.jobs || [];
  }

  // ==================== Async Large Exports ====================
  // Use these for months/years of historical data. The sync /reports/export
  // path streams but holds an HTTP connection — async path returns a jobId
  // immediately and the file persists in MongoDB GridFS until downloaded.

  async enqueueExportJob({ sql, format, filename, dbEngine = 'mysql', mysqlDatabase, plantId, scheduledReportId }) {
    const res = await fetch(`${API_URL}/reports/export/job`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ sql, format, filename, dbEngine, mysqlDatabase, plantId, scheduledReportId })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || `HTTP ${res.status}`);
    return data; // { jobId, status }
  }

  async getExportJob(jobId) {
    const res = await fetch(`${API_URL}/reports/export/job/${jobId}`, {
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.job;
  }

  async cancelExportJob(jobId) {
    const res = await fetch(`${API_URL}/reports/export/job/${jobId}/cancel`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data;
  }

  async downloadExportJob(jobId) {
    const res = await fetch(`${API_URL}/reports/export/job/${jobId}/download`, {
      headers: this.getAuthHeaders()
    });
    if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
    return await res.blob();
  }

  async downloadReportFile(fileId) {
    const res = await fetch(`${API_URL}/reports/files/${fileId}/download`, {
      headers: this.getAuthHeaders()
    });
    if (!res.ok) throw new Error('Download failed');
    return await res.blob();
  }

  async deleteReportFile(fileId) {
    const res = await fetch(`${API_URL}/reports/files/${fileId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data;
  }

  // ==================== Utilities ====================

  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  isElectron() {
    return !!window.electronAPI;
  }
}

export default new ReportService();
